import { addMonths, differenceInCalendarDays, max, min } from 'date-fns'
import {
  all,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import {
  changeDateScrollable,
  changedDateManual,
  changedDateScrollable,
  IChangeDateScrollableAction,
  IChangeManualDateAction,
} from '../../actions/bookingCalendar/date/changeDateRange'
import { isTouchDevice } from '../../components/MediaQuery'
import {
  CALENDAR_EXTEND_LEFT,
  CALENDAR_EXTEND_RIGHT,
  CALENDAR_SCROLL_THRESHOLD,
} from '../../reducers/bookingCalendar/initialState'
import {
  CalendarActions,
  DateActions,
} from '../../reducers/bookingCalendar/actionNames'
import {
  getFromDate,
  getIsFullyLoaded,
  getScrollableFromDate,
  getScrollableToDate,
  getToDate,
} from '../../reducers/bookingCalendar/selectors/dates'
import { getScrollDragging } from '../../reducers/bookingCalendar/selectors/getScrollTop'

function* expandRange(action: IChangeDateScrollableAction) {
  // const [unloadedSelector, from, to] = yield all([
  //   select(getUnloadedForRange),
  //   select(getScrollableFromDate),
  //   select(getScrollableToDate),
  // ])
  // const rangeToLoad = unloadedSelector({
  //   from: min(from, action.startDate),
  //   to: max(action.finishDate, to),
  // })
  //
  // if (rangeToLoad) {
  //   yield put(requestDataForCalendarEvents(rangeToLoad.from, rangeToLoad.to))
  // }
  yield put(changedDateScrollable(action.startDate, action.finishDate))
}

function* expander() {
  const [startDate, finishDate, scrollableFrom, scrollableTo] = yield all([
    select(getFromDate),
    select(getToDate),
    select(getScrollableFromDate),
    select(getScrollableToDate),
  ])

  if (
    differenceInCalendarDays(startDate, scrollableFrom) <
    CALENDAR_SCROLL_THRESHOLD
  ) {
    yield put(
      changeDateScrollable(
        addMonths(min(scrollableFrom, startDate), -CALENDAR_EXTEND_LEFT),
        scrollableTo,
      ),
    )
    yield take(DateActions.ScrollableDateRangeChanged)
  } else if (
    differenceInCalendarDays(scrollableTo, finishDate) <
    CALENDAR_SCROLL_THRESHOLD
  ) {
    yield put(
      changeDateScrollable(
        scrollableFrom,
        addMonths(max(scrollableTo, finishDate), CALENDAR_EXTEND_RIGHT),
      ),
    )
    yield take(DateActions.ScrollableDateRangeChanged)
  }
}

function* expandRangeIfNecessary() {
  const isDragging = yield select(getScrollDragging)
  const isFullyLoaded = yield select(getIsFullyLoaded)

  if (!isDragging && isFullyLoaded) {
    const [startDate, finishDate, scrollableFrom, scrollableTo] = yield all([
      select(getFromDate),
      select(getToDate),
      select(getScrollableFromDate),
      select(getScrollableToDate),
    ])

    return (
      differenceInCalendarDays(startDate, scrollableFrom) <
      CALENDAR_SCROLL_THRESHOLD ||
      differenceInCalendarDays(scrollableTo, finishDate) <
      CALENDAR_SCROLL_THRESHOLD
    )
  }

  return false
}

function* expandRangeManual(action: IChangeManualDateAction) {
  const { startDate } = action

  yield* expander()
  yield put(changedDateManual(startDate))
}

export default function* () {
  yield takeLatest(DateActions.ScrollableDateRangeChanging, expandRange)
  yield takeLatest(DateActions.ChangeManualDate, expandRangeManual)

  while (true) {
    if (!isTouchDevice) {
      yield take([
        DateActions.ChangeDateRangeRendered,
        CalendarActions.SetScrollDragging,
      ])

      const isNecessary = yield* expandRangeIfNecessary()
      if (isNecessary) {
        yield* expander()
      }
    } else {
      yield take(CalendarActions.startScrolling)
      while (true) {
        yield take([
          DateActions.ChangeDateRangeRendered,
          CalendarActions.SetScrollDragging,
        ])

        const isNecessary = yield* expandRangeIfNecessary()
        if (isNecessary) {
          let sufficientlyFinished = false
          while (!sufficientlyFinished) {
            yield take(CalendarActions.finishScrolling)
            const { delayed } = yield race({
              delayed: delay(300),
              scroll: take(CalendarActions.startScrolling),
            })

            if (delayed) {
              sufficientlyFinished = true
            }
          }
          yield* expander()
          break
        }
      }
    }
  }
}
